import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { EU_PROGRAMMES } from "@/data/euProgrammes";

/**
 * /programmes — Illustrative Sourcing Programmes index.
 * Groups the 10 European illustrative programmes by market.
 */
export const ProgrammesIndex = () => {
  useSeo({
    title:
      "Illustrative Sourcing Programmes — UK · Germany · Netherlands · Nordics | Asia Sourcing",
    description:
      "Ten illustrative European sourcing programmes across UK, Germany, Netherlands and Nordic markets. Capability-based educational scenarios covering furniture, home décor, lighting, kitchenware, supplier verification, QC and responsible sourcing.",
    path: "/programmes",
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/programmes#collection`,
          name: "Illustrative Sourcing Programmes",
          description:
            "Capability-based illustrative sourcing programmes across UK, Germany, Netherlands and Nordic markets.",
          url: `${SITE_URL}/programmes`,
          hasPart: EU_PROGRAMMES.map((p) => ({
            "@type": "Article",
            name: p.h1,
            url: `${SITE_URL}/programmes/${p.slug}`,
          })),
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Illustrative Programmes", path: "/programmes" },
        ]),
      ],
    },
    jsonLdId: "programmes-index-jsonld",
  });

  const markets = ["United Kingdom", "Germany", "Netherlands", "Nordic markets"];

  return (
    <article data-testid="programmes-index-page">
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}>
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }}>Home</Link> · Illustrative Programmes
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="programmes-index-eyebrow">
            Illustrative Sourcing Programmes · European markets
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text="Ten illustrative sourcing programmes across UK, Germany, Netherlands and the Nordics."
          className="display tp-title"
          stagger={70}
          testId="programmes-index-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
          data-testid="programmes-index-lede"
        >
          These are capability-based illustrative sourcing programmes — not real
          client engagements. Each documents the operational workflow Asia
          Sourcing actually runs for a specific market × category combination,
          using authentic Indian manufacturing clusters and realistic buyer
          archetypes. For real programme retrospectives with anonymised clients,
          see our <Link to="/case-studies" style={{ color: "inherit", textDecoration: "underline" }}>case studies</Link>.
        </Reveal>
      </header>

      {markets.map((market) => {
        const items = EU_PROGRAMMES.filter((p) => p.country === market);
        if (items.length === 0) return null;
        return (
          <section
            key={market}
            className="wrap"
            data-testid={`programmes-market-${market.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              paddingBlock: "clamp(2rem, 4vw, 3rem)",
              borderTop: "1px solid var(--line)",
            }}
          >
            <p
              className="mono"
              style={{
                color: "var(--brass-deep)",
                fontSize: ".72rem",
                letterSpacing: ".14em",
                textTransform: "uppercase",
                margin: "0 0 .6rem",
              }}
            >
              {market} · {items.length} programme{items.length > 1 ? "s" : ""}
            </p>
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.4rem",
                letterSpacing: "-.01em",
              }}
            >
              Illustrative programmes for {market} buyers
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "1rem",
              }}
            >
              {items.map((p) => (
                <Link
                  key={p.slug}
                  to={`/programmes/${p.slug}`}
                  data-testid={`programmes-card-${p.slug}`}
                  style={{
                    display: "block",
                    padding: "1.3rem 1.4rem",
                    border: "1px solid var(--line)",
                    color: "var(--ink)",
                    textDecoration: "none",
                    background: "var(--paper)",
                  }}
                >
                  <p
                    className="mono"
                    style={{
                      color: "var(--brass-deep)",
                      fontSize: ".62rem",
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      margin: "0 0 .5rem",
                    }}
                  >
                    {p.category} · Cluster {p.cluster}
                  </p>
                  <p
                    style={{
                      fontFamily: "var(--display)",
                      fontSize: "1.08rem",
                      letterSpacing: "-.005em",
                      margin: "0 0 .6rem",
                      lineHeight: 1.35,
                    }}
                  >
                    {p.h1} →
                  </p>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </article>
  );
};

export default ProgrammesIndex;
